import { useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Globe } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { prettyTimeZone, timeZoneOffsetLabel } from '@/lib/calendars';
import { listTimeZones } from '@/lib/timezone';
import { cn } from '@/lib/utils';

/**
 * IANA zone picker for a calendar (mirrors the Preferences timezone card, minus the "system
 * default" row — a calendar always has an explicit zone). The trigger shows the zone plus its
 * current UTC offset so a pick is verifiable in place.
 */
export function TimezoneSelect({ value = '', onChange, disabled = false, invalid = false, ariaLabel = 'Timezone' }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => listTimeZones(), []);
  const current = String(value || '').trim();
  // A stored zone that this runtime does not list (older ICU, exotic alias) stays selectable.
  const items = useMemo(() => (current && !zones.includes(current) ? [current, ...zones] : zones), [zones, current]);
  const offset = timeZoneOffsetLabel(current);
  // ~420 rows × an Intl.DateTimeFormat each, re-rendered on every keystroke of the filter —
  // memoize per zone for the life of the picker (DST flips mid-session are not worth a miss).
  const offsetCacheRef = useRef(new Map());
  const offsetFor = (zone) => {
    const cache = offsetCacheRef.current;
    if (!cache.has(zone)) cache.set(zone, timeZoneOffsetLabel(zone));
    return cache.get(zone);
  };

  const pick = (zone) => {
    onChange?.(zone);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-left text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60',
            invalid ? 'ring-1 ring-destructive' : '',
          )}
        >
          <Globe className={cn('h-4 w-4 shrink-0', current ? 'text-primary' : 'text-muted-foreground')} aria-hidden />
          <span className={cn('flex-1 truncate', current ? 'text-foreground' : 'text-muted-foreground')}>
            {current ? prettyTimeZone(current) : 'Pick a timezone'}
          </span>
          {offset ? <span className="shrink-0 text-[12px] font-semibold text-muted-foreground">{offset}</span> : null}
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <Command>
          <CommandInput placeholder="Search timezones…" />
          <CommandList>
            <CommandEmpty>No matching timezone.</CommandEmpty>
            <CommandGroup>
              {items.map((zone) => (
                <CommandItem key={zone} value={`${zone} ${prettyTimeZone(zone)}`} onSelect={() => pick(zone)}>
                  <Check className={cn('h-4 w-4', current === zone ? 'opacity-100' : 'opacity-0')} aria-hidden />
                  <span className="min-w-0 flex-1 truncate">{prettyTimeZone(zone)}</span>
                  <span className="shrink-0 text-[11px] font-semibold text-muted-foreground">{offsetFor(zone)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
